'use client';

import { SvgIcon } from '@/components/svgIcon';
import ChevronDown from '@/public/icons/arrow-drop-down-filled.svg';
import { Select } from '@base-ui/react/select';
import classNames from 'classnames';
import * as React from 'react';

import styles from './formSelect.module.scss';

export type FormSelectOption = {
  value: string;
  label: React.ReactNode;
  'data-testid'?: string;
};

export interface FormSelectProps {
  id: string;
  name: string;
  labelId: string;
  label: React.ReactNode;
  required?: boolean;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  options: FormSelectOption[];
  error?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  size?: 'small' | 'medium';
  /** Larger trigger (newsletter tile `sizeLarge`) */
  density?: 'default' | 'large';
  inputProps?: { 'data-testid'?: string };
  className?: string;
  placeholder?: React.ReactNode;
  displayEmpty?: boolean;
  endAdornment?: React.ReactNode;
}

export function FormSelect({
  id,
  name,
  labelId,
  label,
  required = false,
  value,
  onChange,
  onBlur,
  options,
  error = false,
  disabled = false,
  readOnly = false,
  size = 'medium',
  density = 'default',
  inputProps,
  className,
  placeholder,
  displayEmpty = false,
  endAdornment,
}: FormSelectProps) {
  const dataTestId = inputProps?.['data-testid'];
  const [listOpen, setListOpen] = React.useState(false);

  const handleValueChange = React.useCallback(
    (next: string | null) => {
      const v = next ?? '';
      onChange({
        target: { name, value: v },
      } as React.ChangeEvent<HTMLInputElement>);
    },
    [name, onChange]
  );

  const handleBlur = React.useCallback(() => {
    onBlur?.({ target: { name } } as React.FocusEvent<HTMLInputElement>);
  }, [name, onBlur]);

  const disabledFlag = Boolean(disabled);
  const readOnlyFlag = Boolean(readOnly);

  return (
    <div
      className={classNames(
        className,
        density === 'large' ? 'sizeLarge' : null,
        endAdornment ? 'selectHasInfoText' : null
      )}
      data-swd-textfield=""
      data-disabled={disabledFlag ? 'true' : 'false'}
      data-error={error ? 'true' : 'false'}
      data-focused={listOpen ? 'true' : 'false'}
      data-full-width="true"
      data-multiline="false"
      data-readonly={readOnlyFlag ? 'true' : 'false'}
      data-size={size === 'small' ? 'small' : 'medium'}
      data-variant="outlined"
    >
      <label
        data-disabled={disabledFlag ? 'true' : undefined}
        data-shrink="true"
        data-slot="label"
        htmlFor={id}
        id={labelId}
      >
        {label}
        {required ? ' *' : ''}
      </label>

      <div
        data-disabled={disabledFlag ? 'true' : undefined}
        data-error={error ? 'true' : 'false'}
        data-has-end-adornment={endAdornment ? 'true' : undefined}
        data-readonly={readOnlyFlag ? 'true' : 'false'}
        data-slot="outlined-root"
      >
        <div aria-hidden data-slot="notched-outline" />
        <div data-multiline="false" data-slot="input-row">
          <div className={styles.selectControl}>
            <Select.Root
              disabled={disabledFlag}
              name={name}
              onOpenChange={setListOpen}
              onValueChange={handleValueChange}
              readOnly={readOnlyFlag}
              required={required}
              value={value}
            >
              <Select.Trigger
                aria-labelledby={labelId}
                className={styles.selectTrigger}
                data-readonly={readOnlyFlag ? 'true' : undefined}
                data-slot="input"
                data-testid={dataTestId}
                disabled={disabledFlag}
                id={id}
                onBlur={handleBlur}
              >
                <span className={styles.value}>
                  <Select.Value>
                    {(v: string | null) => {
                      const str = v ?? '';
                      if (!str && displayEmpty && placeholder) {
                        return <span className={styles.placeholder}>{placeholder}</span>;
                      }
                      const found = options.find((o) => o.value === str);
                      return found?.label ?? str;
                    }}
                  </Select.Value>
                </span>
                <Select.Icon className={styles.chevron}>
                  <SvgIcon aria-hidden component={ChevronDown} />
                </Select.Icon>
              </Select.Trigger>

              <Select.Portal>
                <Select.Backdrop className={styles.backdrop} />
                <Select.Positioner
                  align="start"
                  alignItemWithTrigger={false}
                  className={styles.positioner}
                  side="bottom"
                  sideOffset={4}
                >
                  <Select.Popup className={styles.popup} data-swd-select-popup="">
                    <Select.List>
                      {options.map((option) => (
                        <Select.Item
                          key={option.value}
                          className={styles.item}
                          data-testid={option['data-testid']}
                          value={option.value}
                        >
                          <div className={styles.itemContent}>
                            <Select.ItemText>{option.label}</Select.ItemText>
                          </div>
                        </Select.Item>
                      ))}
                    </Select.List>
                  </Select.Popup>
                </Select.Positioner>
              </Select.Portal>
            </Select.Root>
          </div>
          {endAdornment ? (
            <div className={styles.endAdornment} data-slot="end-adornment">
              {endAdornment}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
